import faker from 'faker';
import { Plus, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';

faker.seed(91);

type EntityType = 'Individual' | 'Organization' | 'Vessel' | 'Aircraft';
type Source = 'Internal' | 'OFAC' | 'UN' | 'EU';

type WhitelistEntry = {
	id: string;
	name: string;
	entityType: EntityType;
	source: Source;
	reason: string;
	addedBy: string;
	date: string;
};

const ENTITY_TYPES: EntityType[] = [
	'Individual',
	'Organization',
	'Vessel',
	'Aircraft',
];
const SOURCES: Source[] = ['Internal', 'OFAC', 'UN', 'EU'];

function randomEntityType(): EntityType {
	return ENTITY_TYPES[
		faker.datatype.number({ min: 0, max: ENTITY_TYPES.length - 1 })
	];
}

function randomSource(): Source {
	return SOURCES[faker.datatype.number({ min: 0, max: SOURCES.length - 1 })];
}

const INITIAL_ENTRIES: WhitelistEntry[] = Array.from({ length: 20 }, () => ({
	id: faker.datatype.uuid(),
	name: faker.name.findName(),
	entityType: randomEntityType(),
	source: randomSource(),
	reason: faker.lorem.sentence(),
	addedBy: faker.name.findName(),
	date: faker.date.past(2).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	}),
}));

function entityTypeBadge(et: EntityType) {
	switch (et) {
		case 'Individual':
			return (
				<Badge className="border border-blue-200 bg-blue-50 text-blue-700 text-xs hover:bg-blue-50">
					Individual
				</Badge>
			);
		case 'Organization':
			return (
				<Badge className="border border-purple-200 bg-purple-50 text-purple-700 text-xs hover:bg-purple-50">
					Organization
				</Badge>
			);
		case 'Vessel':
			return (
				<Badge className="border border-cyan-200 bg-cyan-50 text-cyan-700 text-xs hover:bg-cyan-50">
					Vessel
				</Badge>
			);
		case 'Aircraft':
			return (
				<Badge className="border border-orange-200 bg-orange-50 text-orange-700 text-xs hover:bg-orange-50">
					Aircraft
				</Badge>
			);
	}
}

function sourceBadge(source: Source) {
	switch (source) {
		case 'Internal':
			return (
				<Badge variant="secondary" className="text-xs">
					Internal
				</Badge>
			);
		case 'OFAC':
			return (
				<Badge className="border border-red-200 bg-red-50 text-red-700 text-xs hover:bg-red-50">
					OFAC
				</Badge>
			);
		case 'UN':
			return (
				<Badge className="border border-green-200 bg-green-50 text-green-700 text-xs hover:bg-green-50">
					UN
				</Badge>
			);
		case 'EU':
			return (
				<Badge className="border border-yellow-200 bg-yellow-50 text-yellow-700 text-xs hover:bg-yellow-50">
					EU
				</Badge>
			);
	}
}

type AddForm = {
	name: string;
	entityType: EntityType | '';
	source: Source | '';
	reason: string;
};

const EMPTY_FORM: AddForm = {
	name: '',
	entityType: '',
	source: '',
	reason: '',
};

export default function WhitelistPage() {
	const [entries, setEntries] = useState<WhitelistEntry[]>(INITIAL_ENTRIES);
	const [search, setSearch] = useState('');
	const [entityTypeFilter, setEntityTypeFilter] = useState('all');
	const [sourceFilter, setSourceFilter] = useState('all');
	const [dialogOpen, setDialogOpen] = useState(false);
	const [form, setForm] = useState<AddForm>(EMPTY_FORM);

	const filtered = useMemo(() => {
		return entries.filter((e) => {
			const matchSearch = e.name.toLowerCase().includes(search.toLowerCase());
			const matchType =
				entityTypeFilter === 'all' || e.entityType === entityTypeFilter;
			const matchSource = sourceFilter === 'all' || e.source === sourceFilter;
			return matchSearch && matchType && matchSource;
		});
	}, [entries, search, entityTypeFilter, sourceFilter]);

	const handleRemove = (id: string) => {
		setEntries((prev) => prev.filter((e) => e.id !== id));
		toast.success('Entry removed from whitelist');
	};

	const handleAdd = () => {
		if (!form.name || !form.entityType || !form.source) {
			toast.error('Please fill in all required fields');
			return;
		}
		const newEntry: WhitelistEntry = {
			id: `wl_${Date.now()}`,
			name: form.name,
			entityType: form.entityType as EntityType,
			source: form.source as Source,
			reason: form.reason,
			addedBy: 'Alex Morgan',
			date: new Date().toLocaleDateString('en-US', {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			}),
		};
		setEntries((prev) => [newEntry, ...prev]);
		setForm(EMPTY_FORM);
		setDialogOpen(false);
		toast.success('Entry added to whitelist');
	};

	return (
		<div className="min-h-screen bg-background">
			<Toaster richColors position="top-right" />
			<div className="border-b border-border bg-white px-6 py-4">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-xl font-semibold text-[#0A0A0A]">Whitelist</h1>
						<p className="mt-0.5 text-sm text-[#737373]">
							Manage whitelisted entities excluded from AML screening alerts
						</p>
					</div>
					<Button
						size="sm"
						className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A]/90"
						onClick={() => setDialogOpen(true)}
					>
						<Plus className="mr-1.5 h-4 w-4" />
						Add Entry
					</Button>
				</div>
			</div>

			<div className="p-6 flex flex-col gap-4">
				<div className="flex items-center gap-3">
					<div className="relative flex-1 max-w-sm">
						<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#737373]" />
						<Input
							placeholder="Search by name..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="pl-9"
						/>
					</div>
					<Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
						<SelectTrigger className="w-[180px]">
							<SelectValue placeholder="Entity Type" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Types</SelectItem>
							{ENTITY_TYPES.map((t) => (
								<SelectItem key={t} value={t}>
									{t}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Select value={sourceFilter} onValueChange={setSourceFilter}>
						<SelectTrigger className="w-[160px]">
							<SelectValue placeholder="Source" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Sources</SelectItem>
							{SOURCES.map((s) => (
								<SelectItem key={s} value={s}>
									{s}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className="overflow-hidden rounded-xl border border-[#e5e5e5] bg-white">
					<Table>
						<TableHeader>
							<TableRow className="bg-[#fafafa]">
								<TableHead className="text-xs font-semibold text-[#737373]">
									Name
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Entity Type
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Source
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Reason
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Added By
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Date
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Actions
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filtered.map((entry) => (
								<TableRow key={entry.id}>
									<TableCell className="text-sm font-medium text-[#0a0a0a]">
										{entry.name}
									</TableCell>
									<TableCell>{entityTypeBadge(entry.entityType)}</TableCell>
									<TableCell>{sourceBadge(entry.source)}</TableCell>
									<TableCell className="text-sm text-[#525252] max-w-[220px] truncate">
										{entry.reason}
									</TableCell>
									<TableCell className="text-sm text-[#525252]">
										{entry.addedBy}
									</TableCell>
									<TableCell className="text-sm text-[#525252]">
										{entry.date}
									</TableCell>
									<TableCell>
										<Button
											variant="ghost"
											size="sm"
											className="text-red-600 hover:bg-red-50 hover:text-red-700"
											onClick={() => handleRemove(entry.id)}
										>
											<Trash2 className="h-4 w-4" />
										</Button>
									</TableCell>
								</TableRow>
							))}
							{filtered.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={7}
										className="py-10 text-center text-sm text-[#737373]"
									>
										No entries found.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</div>

			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent className="sm:max-w-[480px]">
					<DialogHeader>
						<DialogTitle>Add Whitelist Entry</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-4 py-2">
						<div className="flex flex-col gap-1.5">
							<Label htmlFor="wl-name">
								Name <span className="text-red-500">*</span>
							</Label>
							<Input
								id="wl-name"
								placeholder="Full name or entity name"
								value={form.name}
								onChange={(e) =>
									setForm((f) => ({ ...f, name: e.target.value }))
								}
							/>
						</div>
						<div className="flex flex-col gap-1.5">
							<Label>
								Entity Type <span className="text-red-500">*</span>
							</Label>
							<Select
								value={form.entityType}
								onValueChange={(v) =>
									setForm((f) => ({ ...f, entityType: v as EntityType }))
								}
							>
								<SelectTrigger>
									<SelectValue placeholder="Select entity type" />
								</SelectTrigger>
								<SelectContent>
									{ENTITY_TYPES.map((t) => (
										<SelectItem key={t} value={t}>
											{t}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-1.5">
							<Label>
								Source <span className="text-red-500">*</span>
							</Label>
							<Select
								value={form.source}
								onValueChange={(v) =>
									setForm((f) => ({ ...f, source: v as Source }))
								}
							>
								<SelectTrigger>
									<SelectValue placeholder="Select source" />
								</SelectTrigger>
								<SelectContent>
									{SOURCES.map((s) => (
										<SelectItem key={s} value={s}>
											{s}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-1.5">
							<Label htmlFor="wl-reason">Reason</Label>
							<Textarea
								id="wl-reason"
								placeholder="Reason for whitelisting..."
								rows={3}
								value={form.reason}
								onChange={(e) =>
									setForm((f) => ({ ...f, reason: e.target.value }))
								}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDialogOpen(false)}>
							Cancel
						</Button>
						<Button
							className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A]/90"
							onClick={handleAdd}
						>
							Add Entry
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
